import { useState, useEffect } from "react";
import {
  Typography, Button, Stack, Box, Card, CardContent,
  IconButton, Chip, Dialog, DialogTitle, DialogContent,
  DialogActions, MenuItem, Select, FormControl, InputLabel, CircularProgress,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DeleteIcon from "@mui/icons-material/Delete";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import RequestQuoteIcon from "@mui/icons-material/RequestQuote";
import {
  collection, onSnapshot, doc, updateDoc, deleteDoc,
} from "firebase/firestore";
import { db } from "../firebase/config";

interface OrderQuote {
  id?: string;
  code: string;
  clientName: string;
  companyName: string;
  phone: string;
  email?: string;
  city: string;
  notes?: string;
  productId: string;
  productName: string;
  selectedColor: string;
  customizationId: string;
  customizationName: string;
  quantities: { [size: string]: number };
  totalUnits: number;
  unitPrice: number;
  customizationPrice: number;
  discountPct: number;
  discountAmount: number;
  totalPrice: number;
  status: "pendiente" | "en_proceso" | "en_confeccion" | "despachado" | "entregado" | "cancelado";
  createdAt?: any;
}

const statusColors: Record<string, "warning" | "info" | "primary" | "secondary" | "success" | "error"> = {
  pendiente: "warning",
  en_proceso: "info",
  en_confeccion: "primary",
  despachado: "secondary",
  entregado: "success",
  cancelado: "error",
};

export default function DistripakoCotizacionesPage() {
  const [quotes, setQuotes] = useState<OrderQuote[]>([]);
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<OrderQuote | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsub = onSnapshot(collection(db, "cotizaciones"), (snap) => {
      const data = snap.docs.map((d) => ({ id: d.id, ...d.data() } as OrderQuote));
      // Ordenar por fecha o ID
      setQuotes(data);
      setLoading(false);
    });
    return () => unsub();
  }, []);

  const handleStatusChange = async (id: string, newStatus: string) => {
    await updateDoc(doc(db, "cotizaciones", id), { status: newStatus });
    if (selected && selected.id === id) {
      setSelected((prev) => prev ? { ...prev, status: newStatus as any } : null);
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm("¿Eliminar esta solicitud de cotización?")) {
      await deleteDoc(doc(db, "cotizaciones", id));
      if (selected?.id === id) setOpen(false);
    }
  };

  const openWhatsApp = (quote: OrderQuote) => {
    const cleanPhone = quote.phone.replace(/[^0-9]/g, "");
    const phoneWithCode = cleanPhone.startsWith("57") ? cleanPhone : `57${cleanPhone}`;
    const msg = encodeURIComponent(`Hola ${quote.clientName}, te contactamos de Distripako sobre tu cotización ${quote.code || ""} de ${quote.productName}.`);
    window.open(`https://wa.me/${phoneWithCode}?text=${msg}`, "_blank");
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: "auto" }}>
      {/* Header */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h5" fontWeight={800}>📋 Cotizaciones Web Recibidas</Typography>
          <Typography variant="body2" color="text.secondary">
            Solicitudes enviadas por clientes a través del Cotizador Interactivo del sitio web
          </Typography>
        </Box>
      </Stack>

      {loading ? (
        <Box display="flex" justifyContent="center" py={8}><CircularProgress /></Box>
      ) : (
        <TableContainer component={Paper} sx={{ borderRadius: 3, border: "1px solid #e2e8f0", boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}>
          <Table>
            <TableHead sx={{ bgcolor: "#f8fafc" }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 800 }}>Código</TableCell>
                <TableCell sx={{ fontWeight: 800 }}>Cliente / Empresa</TableCell>
                <TableCell sx={{ fontWeight: 800 }}>Producto</TableCell>
                <TableCell sx={{ fontWeight: 800 }}>Cantidad</TableCell>
                <TableCell sx={{ fontWeight: 800 }}>Total Estimado</TableCell>
                <TableCell sx={{ fontWeight: 800 }}>Estado</TableCell>
                <TableCell sx={{ fontWeight: 800, textAlign: "right" }}>Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {quotes.map((q) => (
                <TableRow key={q.id} hover>
                  <TableCell sx={{ fontWeight: 700 }}>{q.code || "DP-WEB"}</TableCell>
                  <TableCell>
                    <Typography fontWeight={700} fontSize="0.9rem">{q.clientName}</Typography>
                    <Typography variant="caption" color="text.secondary">{q.companyName} • {q.city}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography fontWeight={600} fontSize="0.88rem">{q.productName}</Typography>
                    <Typography variant="caption" color="text.secondary">{q.customizationName}</Typography>
                  </TableCell>
                  <TableCell>
                    <Chip label={`${q.totalUnits || 0} unds`} size="small" variant="outlined" sx={{ fontWeight: 700 }} />
                  </TableCell>
                  <TableCell sx={{ fontWeight: 800, color: "primary.main" }}>
                    ${Number(q.totalPrice || 0).toLocaleString("es-CO")} COP
                  </TableCell>
                  <TableCell>
                    <Chip label={q.status || "pendiente"} size="small" color={statusColors[q.status] || "default"} sx={{ textTransform: "capitalize", fontWeight: 700 }} />
                  </TableCell>
                  <TableCell sx={{ textAlign: "right" }}>
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                      <IconButton size="small" color="success" onClick={() => openWhatsApp(q)} title="Abrir WhatsApp">
                        <WhatsAppIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" color="primary" onClick={() => { setSelected(q); setOpen(true); }} title="Ver Detalle">
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" color="error" onClick={() => handleDelete(q.id!)} title="Eliminar">
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
              {quotes.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} sx={{ textAlign: "center", py: 6, color: "text.secondary" }}>
                    <RequestQuoteIcon sx={{ fontSize: 48, opacity: 0.2, mb: 1, display: "block", mx: "auto" }} />
                    <Typography>Aún no hay cotizaciones registradas desde el sitio web.</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Dialog Detalle */}
      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm"
        PaperProps={{ sx: { borderRadius: 4 } }}>
        <DialogTitle sx={{ fontWeight: 800 }}>
          📄 Detalle de Cotización {selected?.code}
        </DialogTitle>
        <DialogContent>
          {selected && (
            <Stack spacing={2.5} mt={1}>
              <Box sx={{ p: 2, bgcolor: "#f8fafc", borderRadius: 2 }}>
                <Typography variant="overline" color="primary" fontWeight={800}>Datos del Cliente</Typography>
                <Typography><strong>Nombre:</strong> {selected.clientName}</Typography>
                <Typography><strong>Empresa:</strong> {selected.companyName}</Typography>
                <Typography><strong>Teléfono:</strong> {selected.phone}</Typography>
                {selected.email && <Typography><strong>Email:</strong> {selected.email}</Typography>}
                <Typography><strong>Ciudad:</strong> {selected.city}</Typography>
                {selected.notes && <Typography><strong>Observaciones:</strong> {selected.notes}</Typography>}
              </Box>

              <Box sx={{ p: 2, bgcolor: "#f8fafc", borderRadius: 2 }}>
                <Typography variant="overline" color="primary" fontWeight={800}>Detalle de Pedido</Typography>
                <Typography><strong>Prenda:</strong> {selected.productName}</Typography>
                <Typography><strong>Color:</strong> {selected.selectedColor}</Typography>
                <Typography><strong>Personalización:</strong> {selected.customizationName}</Typography>
                <Typography><strong>Cantidad Total:</strong> {selected.totalUnits} unidades</Typography>
                {selected.quantities && (
                  <Box mt={1}>
                    <Typography variant="caption" fontWeight={700}>Tallas:</Typography>
                    <Stack direction="row" gap={1} flexWrap="wrap" mt={0.5}>
                      {Object.entries(selected.quantities).map(([size, qty]) => (
                        <Chip key={size} label={`${size}: ${qty}`} size="small" variant="outlined" />
                      ))}
                    </Stack>
                  </Box>
                )}
                <Typography variant="h6" color="primary" fontWeight={800} mt={2}>
                  Total: ${Number(selected.totalPrice || 0).toLocaleString("es-CO")} COP
                </Typography>
              </Box>

              <FormControl fullWidth>
                <InputLabel>Estado de la Cotización</InputLabel>
                <Select
                  value={selected.status || "pendiente"}
                  label="Estado de la Cotización"
                  onChange={(e) => handleStatusChange(selected.id!, e.target.value)}
                >
                  <MenuItem value="pendiente">Pendiente</MenuItem>
                  <MenuItem value="en_proceso">En Proceso</MenuItem>
                  <MenuItem value="en_confeccion">En Confección</MenuItem>
                  <MenuItem value="despachado">Despachado</MenuItem>
                  <MenuItem value="entregado">Entregado</MenuItem>
                  <MenuItem value="cancelado">Cancelado</MenuItem>
                </Select>
              </FormControl>
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button onClick={() => setOpen(false)} color="inherit">Cerrar</Button>
          {selected && (
            <Button variant="contained" color="success" startIcon={<WhatsAppIcon />} onClick={() => openWhatsApp(selected)} sx={{ borderRadius: 2, px: 3, fontWeight: 800 }}>
              Responder por WhatsApp
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
}
